/**
 * Debounced GET /markets/search — shared by MarketsPage (stock lookup) and
 * OptionsCalculatorPage (optional "link a symbol" underlying price prefill).
 *
 * Debounces `query` by `debounceMs` (default 300) and is race-safe: results
 * from a stale in-flight request are dropped if `query` changes again before
 * it resolves. Returns an empty result set (no request fired) for a blank
 * query.
 */

import { useState, useEffect } from 'react'
import { glorifyApiClient, GlorifyApiError } from '../services/api/glorify'
import type { MarketSearchResult } from '../services/api/glorify'

export interface SymbolSearchState {
  results: MarketSearchResult[]
  isLoading: boolean
  error: string | null
}

export function useSymbolSearch(query: string, debounceMs = 300): SymbolSearchState {
  const [results, setResults] = useState<MarketSearchResult[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    const trimmed = query.trim()
    if (trimmed.length < 1) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setResults([])
      setError(null)
      setIsLoading(false)
      return
    }

    let cancelled = false
    setIsLoading(true)
    const timer = setTimeout(() => {
      void (async () => {
        setError(null)
        try {
          const resp = await glorifyApiClient.marketsSearch(trimmed)
          if (!cancelled) setResults(resp.results)
        } catch (err) {
          if (!cancelled) {
            setError(err instanceof GlorifyApiError ? err.message : 'Search failed')
          }
        } finally {
          if (!cancelled) setIsLoading(false)
        }
      })()
    }, debounceMs)

    return () => {
      cancelled = true
      clearTimeout(timer)
    }
  }, [query, debounceMs])

  return { results, isLoading, error }
}
